import React, {useMemo} from 'react';

import {useLocation, matchPath, useNavigate, useParams, Params} from 'react-router';

import {Anchor, Breadcrumbs as MantineBreadcrumbs} from '@mantine/core';
import {navigateToPathForReason} from '@/modules/navigation/navigation_module';
import {useBatchedSelectors} from '@/store/kysely_module';

type BreadcrumbLink = {label: string; link: string; avoidLink?: boolean};

export type BreadcrumbData = {
    workspaces?: Array<{id: string; name: string}>;
    projects?: Array<{id: string; name: string; workspace_id: string; project_ancestry: string | null}>;
    files?: Array<{id: string; name: string; project_id: string | null}>;
    fileBreadcrumbInfo?: {
        file: {id: string; name: string; workspace_id: string};
        project: {id: string; name: string; workspace_id: string; project_ancestry: string | null};
        workspace: {id: string; name: string};
        parent_projects: Array<{id: string; name: string; workspace_id: string; project_ancestry: string | null}>;
    } | null;
};

type RegisteredBreadcrumbs = {
    pattern: string;
    getBreadcrumbs: (params: Params, data: BreadcrumbData) => BreadcrumbLink[];
}

const registeredBreadcrumbs: RegisteredBreadcrumbs[] = [];

export const registerBreadcrumbs = (
    pattern: RegisteredBreadcrumbs['pattern'],
    getBreadcrumbs: RegisteredBreadcrumbs['getBreadcrumbs']
) => {
    registeredBreadcrumbs.push({pattern, getBreadcrumbs});
};

export const Breadcrumbs = () => {
    const loc = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    const foundBreadcrumbs = useMemo(
        () => registeredBreadcrumbs.find(b => matchPath(b.pattern, loc.pathname)),
        [loc.pathname]
    );

    // Fetch dashboard data which includes workspaces and projects
    // Also fetch file breadcrumb data if on a file page
    const {results} = useBatchedSelectors({
        dashboardData: {
            queryName: 'getDashboardData',
            args: {},
            defaultValue: {files: [], comments: [], projects: [], attachments: [], users: [], myUserActivity: [], projectRevisions: []},
            workspaceContext: {workspace_id: null},
        },
        allWorkspaces: {
            queryName: 'getAllWorkspaces',
            args: {},
            defaultValue: [],
            workspaceContext: {workspace_id: null},
        },
        fileBreadcrumbData: {
            queryName: 'getBreadcrumbInfoForFile',
            args: {file_id: params.fileId || ''},
            defaultValue: null,
            workspaceContext: {workspace_id: params.workspaceId || null, ignore: !params.fileId},
        },
    });

    // Build breadcrumb data from the blob
    const breadcrumbData: BreadcrumbData = useMemo(() => {
        const dashboardData = results.dashboardData.data;
        const allWorkspaces = results.allWorkspaces.data;
        const fileBreadcrumbInfo = results.fileBreadcrumbData.data;

        if (!dashboardData || !allWorkspaces) {
            return {workspaces: [], projects: [], fileBreadcrumbInfo: null};
        }

        const workspaces = allWorkspaces.map(ws => ({
            id: ws.id,
            name: ws.name,
        }));

        const projects = dashboardData.projects.map(p => ({
            id: p.id,
            name: p.name,
            workspace_id: p.workspace_id,
            project_ancestry: p.project_ancestry,
        }));

        return {workspaces, projects, fileBreadcrumbInfo};
    }, [results.dashboardData.data, results.allWorkspaces.data, results.fileBreadcrumbData.data]);

    const breadcrumbLinks = useMemo(() => {
        if (!foundBreadcrumbs) return [];
        return foundBreadcrumbs.getBreadcrumbs(params, breadcrumbData);
    }, [foundBreadcrumbs, params, breadcrumbData]);

    if (!foundBreadcrumbs) {
        return <div style={{height: '20px'}}/>;
    }

    const breadcrumbsStyle = {
        borderBottom: '1px solid',
        paddingBottom: '10px',
        marginBottom: '20px',
        minHeight: '40px',
    };

    if (breadcrumbLinks.length === 0) {
        return <div style={breadcrumbsStyle} />;
    }

    return (
        <MantineBreadcrumbs style={breadcrumbsStyle}>
            {breadcrumbLinks.map((link, i) => (
                link.avoidLink ? (
                    <span
                        key={i}
                    >
                        {link.label}
                    </span>
                ) : (
                    <Anchor
                        key={i}
                        onClick={e => {
                            e.preventDefault();
                            e.stopPropagation();
                            navigateToPathForReason(navigate, 'navigate_to_dynamic_path_from_breadcrumbs', link.link);
                        }}
                    >
                        {link.label}
                    </Anchor>
                )
            ))}
        </MantineBreadcrumbs>
    );
};
